// frontend/src/components/ModalWrapper.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-18
// Updated:      2025-12-17
//
// Description:  Reusable centered modal with dark backdrop and consistent styling.
//               Supports size prop: "md" (default), "lg", "xl", "full" (80% viewport).
//
//               IMPORTANT:
//               - Width is controlled ONLY by the sizeClasses entry.
//               - We do NOT force `w-full` on the dialog container anymore.

import React, { useEffect } from "react";

const sizeClasses = {
  // ✅ Always keep a sensible responsive width and cap
  md: "w-[92vw] max-w-md",
  lg: "w-[92vw] max-w-3xl",
  xl: "w-[92vw] max-w-5xl",

  // ✅ “Big modal” = 80% viewport width (NOT full screen)
  full: "w-[80vw] max-w-[1400px]",
};

const ModalWrapper = ({ children, onClose, size = "md" }) => {
  // Prevent background scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    // High z-index so it always sits above Leaflet panes/controls
    <div className="fixed inset-0 z-[10000]">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} aria-hidden="true" />

      {/* Centered dialog */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div
          role="dialog"
          aria-modal="true"
          className={`pointer-events-auto relative bg-white rounded-lg shadow-lg ${sizeClasses[size]} p-6 font-body max-h-[90vh] overflow-y-auto`}
        >
          <button
            className="
              absolute top-2 right-2 z-10
              w-6 h-6
              rounded-full
              text-white text-sm font-bold
              flex items-center justify-center
              opacity-0 hover:opacity-100
              transition-opacity duration-200
              shadow-md
            "
            style={{ backgroundColor: "var(--secondary-color)" }}
            onClick={onClose}
            title="Close"
          >
            ×
          </button>

          {children}
        </div>
      </div>
    </div>
  );
};

export default ModalWrapper;
